import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Grid, Cell } from 'react-foundation';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';
import DataTable from 'react-data-table-component';

// Demo-specific imports
import { demoConversations } from '../data/demoData';
import DemoFilterComponent from '../components/DemoFilterComponent';
import { demoDataTableColumns } from '../components/DemoDataTableColumns';
import { useDemoContext } from '../../contexts/DemoContext';

// Regular app imports
import { Header, ProgressBar, FlashMessage, ErrorRender } from '../../constants/SharedComponents';
import JourneySteps from '../../components/JourneySteps';

// Styles
import '../../stylesheets/components/forms/Conversations.css';
import '../../stylesheets/components/data-tables/Conversations.css';

export function DemoConversationsIndex() {
  const navigate = useNavigate();
  const location = useLocation();
  const { demoData } = useDemoContext();
  
  // Combine static demo data with user-completed conversations
  const allConversations = React.useMemo(() => [
    ...demoConversations,
    ...(demoData.completedConversations || [])
  ], [demoData.completedConversations]);
  
  // Demo data state
  const [loading] = useState(false);
  const [error] = useState(null);
  const [flashMessageObject, setFlashMessageObject] = useState({});
  
  // Filter state
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  
  // Filter demo data
  let filteredItems = allConversations.filter((item) => {
    return (
      ((item.organiserName?.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.attendeeName?.toLowerCase().includes(filterText.toLowerCase()))) &&
      (filterStatus ? item.status === parseInt(filterStatus) : true)
    );
  });

  const filterTextFunction = useCallback((event) => {
    setFilterText(event.target.value);
  }, []);

  const filterStatusFunction = useCallback((event) => {
    setFilterStatus(event.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setResetPaginationToggle(!resetPaginationToggle);
    setFilterText('');
    setFilterStatus('');
  }, [resetPaginationToggle]);

  const handleCreateConversation = () => {
    navigate('/demo/conversations/new');
  };
  
  // Handle flash messages from navigation state
  React.useEffect(() => {
    if (location.state?.flashMessage) {
      setFlashMessageObject(location.state.flashMessage);
      // Clear the flash message from history state (don't navigate away)
      const state = { ...location.state };
      delete state.flashMessage;
      window.history.replaceState(state, '', location.pathname);
    }
  }, [location]);

  return (
    <>
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      <ErrorRender object={error} />

      {/* Demo Notice */}
      <div className="grid-container">
        <Grid className="grid-padding-x align-justify">
          <Cell small={12}>
            <div style={{ 
              backgroundColor: 'var(--color--green-turquoise--200)', 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid var(--color--green-turquoise--500)'
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--color--green-turquoise--800)' }}>Demo Mode - Sample Data</h3>
              <p style={{ margin: 0, color: 'var(--color--green-turquoise--800)' }}>
                You are viewing sample conversations with demo data. Click on any action buttons to see what they would do in the real application.
                All data shown here is for demonstration purposes only.
              </p>
            </div>
          </Cell>
        </Grid>
      </div>

      <div className="page-content">
        <div className="page-content__header">
          <div className="grid-container">
            <Grid className="grid-padding-x align-justify align-middle">
              <Cell small={12} large={4}>
                <button
                  onClick={handleCreateConversation}
                  id="page-navigation-button--create"
                  className="page-content__button"
                  data-demo-id="demo-create-conversation"
                >
                  <IoChatbubbleEllipsesOutline size={20} style={{ marginRight: '8px' }} />
                  Create Conversation Request
                </button>
              </Cell>
              <Cell small={12} large={8}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <div style={{ width: '100%', maxWidth: '600px' }}>
                    <DemoFilterComponent
                      onFilterText={filterTextFunction}
                      onFilterStatus={filterStatusFunction}
                      onClear={handleClear}
                      filterText={filterText}
                      filterStatus={filterStatus}
                    />
                  </div>
                </div>
              </Cell>
            </Grid>
          </div>
        </div>
      </div>

      {/* Data Table */}
      {allConversations && allConversations.length > 0 && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                * Person who created the conversation
              </div>
            </Cell>
            <Cell large={12} className="js--data-table-wrap">
              <DataTable
                columns={demoDataTableColumns}
                data={filteredItems}
                defaultSortFieldId={3}
                responsive
                fixedHeader
                pagination
                defaultSortAsc={false}
                noDataComponent={
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h3>No conversations match your search criteria</h3>
                    <p>Try adjusting your search terms or filters.</p>
                  </div>
                }
              />
            </Cell>
          </Grid>
        </div>
      )}

      {/* Empty state (if no data) */}
      {(!allConversations || allConversations.length === 0) && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="page-content">
                <h1 className="page-content__title">
                  Welcome to the Forward Together Demo!
                </h1>
                <div className="page-content__styles">
                  <p>
                    <strong>
                      This demo shows how conversations would appear in your dashboard.
                    </strong>
                  </p>
                  <p>
                    Learn more about how to use the tool:&nbsp;
                    <Link to="/pages/how-to-use-this-tool" className="page-content__button">
                      How to use this tool
                    </Link>
                  </p>
                </div>
              </div>
            </Cell>
            <Cell small={12} large={12}>
              <JourneySteps />
            </Cell>
          </Grid>
        </div>
      )}
    </>
  );
}
